import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, X as XIcon } from "lucide-react";
import Logo from "../components/atoms/Logo";
import Chip from "../components/atoms/Chip";
import Button from "../components/atoms/Button";
import Card from "../components/atoms/Card";

const QUESTIONS = [
  { q: "She ___ to school every day.",                         options: ["go", "goes", "going", "gone"],                                            answer: 1, level: "beginner" },
  { q: "I have ___ apple.",                                    options: ["a", "an", "the", "no article"],                                            answer: 1, level: "beginner" },
  { q: "They ___ playing football now.",                       options: ["is", "are", "was", "be"],                                                  answer: 1, level: "beginner" },
  { q: "He ___ already eaten lunch.",                          options: ["have", "has", "had", "is"],                                                answer: 1, level: "intermediate" },
  { q: "If I ___ rich, I would travel the world.",             options: ["am", "was", "were", "be"],                                                 answer: 2, level: "intermediate" },
  { q: "The book ___ by a famous author.",                     options: ["wrote", "was written", "is writing", "has wrote"],                         answer: 1, level: "intermediate" },
  { q: "She asked me where I ___.",                            options: ["live", "lived", "living", "lives"],                                        answer: 1, level: "advanced" },
  { q: "Not only ___ he smart, but he is also kind.",          options: ["is", "was", "does", "has"],                                                answer: 0, level: "advanced" },
  { q: "I wish I ___ studied harder for the exam.",            options: ["have", "has", "had", "would"],                                             answer: 2, level: "advanced" },
  { q: "The project, ___ was completed last month, won an award.", options: ["that", "which", "who", "whom"],                                       answer: 1, level: "ielts" },
];

const LEVEL_TONES = {
  beginner:     { tone: "sage",  spine: "var(--sage)",     label: "Beginner" },
  intermediate: { tone: "gold",  spine: "var(--gold-500)", label: "Intermediate" },
  advanced:     { tone: "rust",  spine: "var(--rust)",     label: "Advanced" },
  ielts:        { tone: "ink",   spine: "var(--ink-800)",  label: "IELTS" },
};

export default function PlacementTest() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [finished, setFinished] = useState(false);

  function selectAnswer(idx) {
    if (selected !== null) return;
    setSelected(idx);
    const isCorrect = idx === QUESTIONS[current].answer;
    setAnswers([...answers, { correct: isCorrect, level: QUESTIONS[current].level }]);
    setTimeout(() => {
      if (current < QUESTIONS.length - 1) {
        setCurrent(current + 1);
        setSelected(null);
      } else {
        setFinished(true);
      }
    }, 800);
  }

  function getResult() {
    const score = answers.filter((a) => a.correct).length;
    const advancedCorrect     = answers.filter((a) => a.correct && (a.level === "advanced" || a.level === "ielts")).length;
    const intermediateCorrect = answers.filter((a) => a.correct && a.level === "intermediate").length;
    if (score >= 8 && advancedCorrect >= 2)        return { key: "advanced",     startDay: 30, label: "Upper intermediate", summary: "Phrasal verbs and conditionals — your area." };
    if (score >= 6 && intermediateCorrect >= 2)    return { key: "intermediate", startDay: 15, label: "Intermediate",       summary: "Tenses and modals next. We'll start at Day 15." };
    if (score >= 3)                                return { key: "intermediate", startDay: 5,  label: "Elementary",         summary: "You've got the basics. Day 5 is where we lift off." };
    return                                                { key: "beginner",     startDay: 1,  label: "Beginner",           summary: "First sounds, first sentences. Day 1 from the top." };
  }

  function applyResult() {
    const result = getResult();
    localStorage.setItem("currentDay", String(result.startDay));
    localStorage.setItem("placementDone", "true");
    const isLoggedIn = !!localStorage.getItem("currentUser");
    navigate(isLoggedIn ? "/dashboard" : "/signup");
  }

  if (finished) {
    const result = getResult();
    const score = answers.filter((a) => a.correct).length;
    const tone = LEVEL_TONES[result.key];
    return (
      <div style={{ minHeight: "100vh", background: "var(--paper)" }}>
        <ShellHeader />
        <main className="max-w-xl mx-auto" style={{ padding: "32px 24px 96px" }}>
          <Card paperDeep spineColor={tone.spine} style={{ paddingTop: 32 }}>
            <Chip tone={tone.tone}>Your level · {tone.label}</Chip>
            <h1 className="font-display" style={{ fontSize: 48, lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "16px 0 12px" }}>
              <em style={{ color: "var(--gold-500)" }}>{result.label}.</em>
            </h1>
            <p style={{ color: "var(--fg-2)", fontSize: 16, lineHeight: 1.6, margin: "0 0 6px" }}>{result.summary}</p>
            <p style={{ color: "var(--fg-3)", fontSize: 14, margin: "0 0 24px" }}>You scored {score}/{QUESTIONS.length} · Starting at Day {result.startDay}.</p>

            <Button variant="primary" onClick={applyResult} style={{ width: "100%", justifyContent: "center" }} iconRight={<ArrowRight size={16} strokeWidth={1.8} />}>
              Start Day {result.startDay}
            </Button>
          </Card>

          <div style={{ marginTop: 32 }}>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--fg-3)", margin: "0 0 12px" }}>
              Your answers
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {answers.map((a, i) => {
                const t = LEVEL_TONES[a.level] || LEVEL_TONES.beginner;
                return (
                  <li key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--border-1)" }}>
                    {a.correct
                      ? <Check size={16} color="var(--sage-dark)" strokeWidth={1.8} />
                      : <XIcon size={16} color="var(--berry)" strokeWidth={1.8} />}
                    <span style={{ fontSize: 13, color: "var(--fg-3)", fontFamily: "var(--font-mono)", minWidth: 36 }}>Q{i + 1}</span>
                    <Chip tone={t.tone}>{t.label}</Chip>
                  </li>
                );
              })}
            </ul>
          </div>
        </main>
      </div>
    );
  }

  const q = QUESTIONS[current];

  return (
    <div style={{ minHeight: "100vh", background: "var(--paper)" }}>
      <ShellHeader />
      <main className="max-w-xl mx-auto" style={{ padding: "32px 24px 96px" }}>
        {/* Step row */}
        <div style={{ display: "flex", gap: 6, marginBottom: 24 }}>
          {QUESTIONS.map((_, i) => {
            const ans = answers[i];
            return (
              <span
                key={i}
                aria-hidden="true"
                style={{
                  flex: 1,
                  height: 3,
                  borderRadius: 2,
                  background:
                    i < current && ans?.correct      ? "var(--sage)"
                  : i < current && ans && !ans.correct ? "var(--berry-light)"
                  : i === current                     ? "var(--gold-500)"
                  :                                     "var(--ink-200)",
                }}
              />
            );
          })}
        </div>

        <Chip tone="ink">Question {current + 1} of {QUESTIONS.length}</Chip>
        <h1 className="font-display" style={{ fontSize: 36, lineHeight: 1.15, fontWeight: 400, letterSpacing: "-0.02em", margin: "12px 0 24px" }}>
          {q.q}
        </h1>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {q.options.map((opt, i) => {
            const isAnswered = selected !== null;
            const isCorrectIdx = i === q.answer;
            const isMine       = i === selected;
            let style = optionRest;
            if (isAnswered && isCorrectIdx)            style = optionCorrect;
            else if (isAnswered && isMine && !isCorrectIdx) style = optionWrong;
            else if (isAnswered)                       style = optionDim;
            return (
              <button
                key={i}
                type="button"
                onClick={() => selectAnswer(i)}
                disabled={isAnswered}
                style={style}
              >
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 999, background: "var(--paper-deep)", color: "var(--ink-900)", fontSize: 12, fontWeight: 700, marginRight: 12, fontFamily: "var(--font-mono)" }}>
                  {String.fromCharCode(65 + i)}
                </span>
                <span style={{ flex: 1, textAlign: "left" }}>{opt}</span>
                {isAnswered && isCorrectIdx && <Check size={16} color="var(--sage-dark)" strokeWidth={1.8} />}
                {isAnswered && isMine && !isCorrectIdx && <XIcon size={16} color="var(--berry)" strokeWidth={1.8} />}
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: 32, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Link to="/" style={{ color: "var(--fg-3)", fontSize: 13, textDecoration: "none" }}>
            Skip for now
          </Link>
          <span style={{ color: "var(--fg-3)", fontSize: 12, fontStyle: "italic", fontFamily: "var(--font-display)" }}>
            Tap an answer — auto-advances
          </span>
        </div>
      </main>
    </div>
  );
}

function ShellHeader() {
  return (
    <header style={{ padding: "20px 24px", maxWidth: 1080, margin: "0 auto", display: "flex", justifyContent: "space-between" }}>
      <Link to="/" style={{ textDecoration: "none" }}><Logo /></Link>
    </header>
  );
}

const optionBase = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  padding: "14px 16px",
  borderRadius: "var(--radius-md)",
  fontFamily: "var(--font-body)",
  fontSize: 15,
  fontWeight: 500,
  cursor: "pointer",
  textAlign: "left",
  transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
};
const optionRest    = { ...optionBase, background: "#fff",            color: "var(--ink-900)",   border: "1px solid var(--border-2)" };
const optionCorrect = { ...optionBase, background: "#eef5ee",         color: "#3d5e3a",          border: "1px solid #cfe0ce", cursor: "default" };
const optionWrong   = { ...optionBase, background: "#fdf0f1",         color: "var(--berry-dark)", border: "1px solid #ddb1b6", cursor: "default" };
const optionDim     = { ...optionBase, background: "var(--paper-deep)", color: "var(--fg-3)",    border: "1px solid var(--border-1)", opacity: 0.6, cursor: "default" };
